import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Mail, Trash2 } from "lucide-react";

const UPDATED = "31 de agosto de 2026";

export const Route = createFileRoute("/lgpd")({
  head: () => ({
    meta: [
      { title: "LGPD — Política de Tratamento de Dados | Kentro" },
      {
        name: "description",
        content:
          "Política de Tratamento e Processamento de Dados da Kentro conforme a LGPD: papéis de Controlador e Operador, canais do omnichannel, suboperadores, segurança, incidentes e exclusão definitiva dos dados em até 1 dia útil após o cancelamento.",
      },
      { property: "og:title", content: "LGPD — Política de Tratamento de Dados | Kentro" },
      {
        property: "og:description",
        content:
          "Como a Kentro trata e processa dados na plataforma de atendimento omnichannel, na condição de Operadora, em conformidade com a Lei 13.709/2018.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://kentro.com.br/lgpd" },
    ],
    links: [{ rel: "canonical", href: "https://kentro.com.br/lgpd" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "LGPD — Política de Tratamento de Dados | Kentro",
          url: "https://kentro.com.br/lgpd",
          inLanguage: "pt-BR",
          dateModified: "2026-08-31",
          publisher: { "@type": "Organization", name: "Kentro Sistemas Inteligentes" },
        }),
      },
    ],
  }),
  component: LgpdPage,
});

function LgpdPage() {
  return (
    <>
      <section className="gradient-hero relative overflow-hidden pt-24 pb-14">
        <div className="circuit-pattern absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/40 px-3 py-1 text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5" /> LGPD · Lei nº 13.709/2018
          </div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl">
            Política de <span className="gradient-text">Tratamento de Dados</span>
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">Última atualização: {UPDATED}.</p>
          <p className="mt-5 max-w-3xl text-base text-muted-foreground">
            Este documento descreve como a{" "}
            <strong className="text-foreground">Kentro Sistemas Inteligentes</strong> (razão social
            Barbalho Solutions Ltda., CNPJ nº 43.406.507/0001-52) trata e processa dados pessoais no
            âmbito do licenciamento da plataforma de gestão de atendimento omnichannel, na condição
            de <strong className="text-foreground">Operadora</strong> de dados do Cliente. Ele
            complementa a{" "}
            <Link to="/privacidade" className="text-primary-glow hover:underline">
              Política de Privacidade
            </Link>
            , os{" "}
            <Link to="/termos-de-uso" className="text-primary-glow hover:underline">
              Termos de Uso
            </Link>{" "}
            e o Contrato de Cessão de Direito de Uso (Licença).
          </p>
          <div className="mt-6 inline-flex items-start gap-3 rounded-xl border border-border bg-background/40 p-4 text-sm text-muted-foreground">
            <Trash2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />
            <p>
              <strong className="text-foreground">Exclusão em até 1 dia útil:</strong> encerrado o
              contrato (após o aviso prévio, normalmente de 30 dias), todos os dados do Cliente na
              plataforma são eliminados de forma definitiva e irrecuperável em até 24 horas úteis.
            </p>
          </div>
        </div>
      </section>

      <article className="mx-auto max-w-4xl px-4 py-14 sm:px-6 lg:px-8">
        <nav aria-label="Sumário" className="mb-12 rounded-2xl border border-border bg-card/40 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Sumário
          </h2>
          <ol className="mt-3 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
            {[
              ["objeto", "Objeto, natureza e finalidade do tratamento"],
              ["papeis", "Papéis das partes: Controlador e Operador"],
              ["dados", "Dados tratados e canais do omnichannel"],
              ["bases", "Bases legais e instruções do Controlador"],
              ["obrigacoes-cliente", "Obrigações do Cliente (Controlador)"],
              ["suboperadores", "Suboperadores e serviços de terceiros"],
              ["transferencia", "Armazenamento e transferência internacional"],
              ["seguranca", "Segurança da informação e governança"],
              ["titulares", "Direitos dos titulares e como exercê-los"],
              ["incidentes", "Incidentes de segurança"],
              ["retencao", "Retenção e exclusão definitiva dos dados"],
              ["responsabilidade", "Responsabilidade entre as partes"],
              ["dpo", "Encarregado (DPO) e contato"],
            ].map(([id, label]) => (
              <li key={id}>
                <a className="hover:text-foreground" href={`#${id}`}>
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div className="prose-kentro space-y-10 text-foreground [&_a]:text-primary-glow [&_strong]:text-foreground">
          <Block id="objeto" title="1. Objeto, natureza e finalidade do tratamento">
            <p>
              <strong>Objeto:</strong> processar e armazenar, em ambiente de nuvem contratado pela
              Kentro, os dados inseridos ou gerados pelo Cliente e por seus usuários na plataforma,
              bem como as mensagens e os registros de atendimento trocados com seus contatos finais
              nos canais conectados.
            </p>
            <p>
              <strong>Natureza e finalidade:</strong> viabilizar a execução do contrato de
              licenciamento — operação do sistema de gestão de atendimento, roteamento de mensagens,
              histórico, automações, relatórios, suporte técnico, segurança e cumprimento de
              obrigações legais e regulatórias.
            </p>
            <p>
              <strong>Duração:</strong> o tratamento ocorre durante a vigência do contrato e se
              encerra com a eliminação dos dados, conforme a seção 11.
            </p>
          </Block>

          <Block id="papeis" title="2. Papéis das partes: Controlador e Operador">
            <ul>
              <li>
                <strong>Cliente = Controlador</strong> (art. 5º, VI, da LGPD): decide quais dados
                insere, para quais finalidades, com qual base legal e por quanto tempo os mantém na
                plataforma.
              </li>
              <li>
                <strong>Kentro = Operadora</strong> (art. 5º, VII, da LGPD): trata os dados{" "}
                <em>em nome e no interesse do Cliente</em>, apenas (i) quando expressamente
                autorizada, (ii) para a execução do contrato e (iii) conforme instruções razoáveis e
                documentadas do Cliente.
              </li>
              <li>
                <strong>Kentro como Controladora:</strong> a Kentro atua como Controladora apenas em
                relação aos dados dos seus próprios clientes e visitantes do site (cadastro,
                faturamento, contato comercial, navegação) — tema tratado na{" "}
                <Link to="/privacidade" className="text-primary-glow hover:underline">
                  Política de Privacidade
                </Link>
                .
              </li>
            </ul>
            <p>
              Caso a Kentro entenda que uma instrução do Cliente possa violar a LGPD ou outra norma
              aplicável, estará desobrigada de executá-la, comunicando ao Cliente, de forma
              justificada, a sua recusa.
            </p>
          </Block>

          <Block id="dados" title="3. Dados tratados e canais do omnichannel">
            <p>
              Os dados tratados são definidos pelo Cliente e tipicamente incluem: dados cadastrais de
              usuários da plataforma (nome, e-mail, vínculo, credenciais); dados de contatos finais
              (nome, telefone/WhatsApp, e-mail, identificadores de perfil em redes sociais);
              conteúdo das conversas e anexos enviados pelos participantes; metadados de atendimento
              (data/hora, fila, responsável, tags, protocolos); e registros técnicos de acesso (IP,
              dispositivo, navegador, logs).
            </p>
            <p>
              O tratamento pode ocorrer nos seguintes canais integrados à plataforma —{" "}
              <strong>WhatsApp, Instagram, Facebook, E-mail, SMS e RCS</strong> — sendo que a Kentro{" "}
              <strong>integra</strong> esses canais, mas eles são serviços operados por terceiros
              (Meta Platforms, provedores de e-mail, operadoras móveis e agregadores de SMS/RCS).
            </p>
            <p>
              <strong>Dados sensíveis e de crianças e adolescentes:</strong> a plataforma não é
              destinada ao tratamento de dados sensíveis nem de dados de crianças e adolescentes. Se
              o Cliente optar por inseri-los, será integralmente responsável pela base legal
              adequada (inclusive consentimento específico e destacado, quando exigido) e pelas
              medidas adicionais de proteção, observado o melhor interesse do titular.
            </p>
            <p>
              <strong>Conteúdo publicado pelo Cliente:</strong> é responsabilidade exclusiva do
              Cliente analisar previamente documentos, arquivos e anexos que envia à plataforma,
              garantir que possui autorização sobre eles e que o uso atende à legislação vigente.
            </p>
          </Block>

          <Block id="bases" title="4. Bases legais e instruções do Controlador">
            <p>
              O Cliente declara que toda coleta e todo compartilhamento de dados com a Kentro
              observam uma base legal da LGPD compatível com a finalidade — em regra execução de
              contrato (art. 7º, V), cumprimento de obrigação legal ou regulatória (art. 7º, II),
              legítimo interesse (art. 7º, IX) ou consentimento (art. 7º, I).
            </p>
            <p>
              Quando o tratamento depender de consentimento (por exemplo, campanhas de marketing por
              SMS, RCS, e-mail ou WhatsApp), o Cliente é o único responsável por obtê-lo de forma
              livre, informada e inequívoca, por manter prova desse consentimento, por informar as
              finalidades ao titular e por atender pedidos de revogação e opt-out.
            </p>
            <p>
              A Kentro trata os dados conforme os princípios do art. 6º da LGPD, com destaque para
              finalidade, adequação, necessidade (minimização), transparência, segurança, prevenção e
              não discriminação.
            </p>
          </Block>

          <Block id="obrigacoes-cliente" title="5. Obrigações do Cliente (Controlador)">
            <ul>
              <li>
                <strong>Análise prévia:</strong> avaliar os dados, arquivos e anexos que serão
                administrados, enviados ou publicados na plataforma.
              </li>
              <li>
                <strong>Controle de acesso:</strong> conceder acesso somente a pessoas autorizadas,
                com credenciais individuais, revogando acessos de usuários desligados.
              </li>
              <li>
                <strong>Medidas técnicas e organizativas:</strong> adotar, no que lhe couber, medidas
                de proteção dos dados pessoais em sua posse, inclusive de terceiros.
              </li>
              <li>
                <strong>Cooperação:</strong> fornecer, quando solicitado, informações, documentos e
                esclarecimentos necessários à análise de segurança, conformidade ou incidente.
              </li>
              <li>
                <strong>Incidentes:</strong> aguardar as correções e atualizações em caso de
                interrupção técnica, cabendo ao Cliente a comunicação com seus próprios titulares e
                terceiros.
              </li>
              <li>
                <strong>Integrações:</strong> responder pelas integrações que habilitar por conta
                própria (CRMs, automações, IA/LLMs), inclusive pela base legal do envio de dados a
                esses terceiros.
              </li>
            </ul>
          </Block>

          <Block id="suboperadores" title="6. Suboperadores e serviços de terceiros">
            <p>
              O Cliente autoriza expressamente a Kentro a subcontratar outros operadores
              (suboperadores) necessários à prestação dos serviços. A Kentro responde pelos atos dos
              suboperadores, que devem seguir as mesmas instruções lícitas e razoáveis e manter
              padrão equivalente de segurança técnica e operacional.
            </p>
            <p>Categorias de suboperadores e terceiros envolvidos:</p>
            <ul>
              <li>
                <strong>Infraestrutura e nuvem:</strong> data centers e provedores de referência
                (como Google Cloud, Oracle e Amazon Web Services) e serviços de CDN, WAF e proteção
                (como Cloudflare);
              </li>
              <li>
                <strong>Canais de mensageria:</strong> Meta Platforms (WhatsApp, Instagram e
                Facebook), provedores de e-mail transacional, operadoras móveis e agregadores de SMS
                e RCS;
              </li>
              <li>
                <strong>Serviços de apoio:</strong> pagamento e emissão fiscal, monitoramento,
                observabilidade, suporte e comunicação, sob obrigações contratuais de
                confidencialidade.
              </li>
            </ul>
            <p>
              A Kentro não possui ingerência sobre as políticas dos canais de terceiros. O Cliente
              deve conhecer e cumprir os termos e as políticas de privacidade de cada provedor que
              optar por utilizar.
            </p>
          </Block>

          <Block id="transferencia" title="7. Armazenamento e transferência internacional">
            <p>
              Os dados são armazenados preferencialmente em regiões de data center no Brasil.
              Eventuais transferências internacionais decorrentes do uso de provedores globais (por
              exemplo, canais de mensageria e serviços de nuvem) observam as hipóteses do art. 33 da
              LGPD, com cláusulas contratuais e salvaguardas equivalentes de proteção.
            </p>
          </Block>

          <Block id="seguranca" title="8. Segurança da informação e governança">
            <ul>
              <li>criptografia em trânsito (TLS) e proteção do ambiente de armazenamento;</li>
              <li>
                controle de acesso baseado em perfis, autenticação individual e princípio do menor
                privilégio;
              </li>
              <li>segregação de ambientes, registros de auditoria e monitoramento contínuo;</li>
              <li>rotinas de backup e planos de continuidade e recuperação;</li>
              <li>
                compromisso de sigilo por colaboradores, prestadores e parceiros que tratam dados;
              </li>
              <li>
                programa de governança em privacidade conduzido pelo Encarregado pela Proteção de
                Dados (DPO).
              </li>
            </ul>
            <p>
              Nenhuma transmissão pela internet é totalmente segura. Em caso de suspeita fundada de
              quebra de segurança, a Kentro pode suspender total ou parcialmente os serviços,
              garantindo transparência sobre o ocorrido.
            </p>
          </Block>

          <Block id="titulares" title="9. Direitos dos titulares e como exercê-los">
            <p>
              Nos termos do art. 18 da LGPD, o titular pode solicitar confirmação da existência de
              tratamento, acesso, correção, anonimização, bloqueio ou eliminação de dados
              desnecessários ou excessivos, portabilidade, informação sobre compartilhamentos,
              revogação do consentimento e oposição ao tratamento.
            </p>
            <p>
              Quando a Kentro atua como <strong>Operadora</strong>, o pedido deve ser dirigido ao
              Cliente (Controlador), que é o único responsável por analisar e responder. Recebida uma
              requisição diretamente, a Kentro informará ao titular sua condição de Operadora e
              redirecionará o pedido ao Cliente em até{" "}
              <strong>5 (cinco) dias úteis</strong>, cooperando no que estiver ao seu alcance.
            </p>
            <p>
              Quando a Kentro atua como <strong>Controladora</strong> (dados de seus clientes e
              visitantes do site), os pedidos podem ser feitos diretamente pelo e-mail{" "}
              <a href="mailto:dpo@kentro.com.br">dpo@kentro.com.br</a>, com resposta nos prazos
              legais.
            </p>
          </Block>

          <Block id="incidentes" title="10. Incidentes de segurança">
            <p>
              Verificado incidente de acesso indevido, não autorizado, vazamento, perda, alteração ou
              destruição de dados pessoais, a Parte que o identificar notificará a outra por escrito
              em até <strong>3 (três) dias úteis</strong> da ciência, informando, na medida do
              possível: data e hora do incidente e da ciência; tipos de dados afetados; número e
              relação de titulares afetados; contato do Encarregado; possíveis consequências; e
              medidas de contenção, reparação e prevenção.
            </p>
            <p>
              Não sendo possível reunir todas as informações, elas serão enviadas de forma gradual,
              devendo a comunicação completa ocorrer em até <strong>10 (dez) dias úteis</strong> da
              ciência, salvo prazo inferior definido em lei ou regulamento da ANPD. As Partes
              providenciarão, em conjunto, as comunicações aos titulares afetados e o relatório à
              ANPD, quando cabível.
            </p>
          </Block>

          <Block id="retencao" title="11. Retenção e exclusão definitiva dos dados">
            <p>
              Durante a vigência do contrato, os dados permanecem armazenados pelo tempo necessário
              às finalidades definidas pelo Cliente, observados os limites de armazenamento do plano
              contratado.
            </p>
            <p>
              <strong>Encerramento do contrato.</strong> A resilição ocorre mediante aviso prévio de,
              normalmente, <strong>30 (trinta) dias</strong>. Cumprido esse prazo e encerrado o
              contrato, os <strong>dados do Cliente</strong> (conversas, contatos, anexos, registros
              de atendimento e demais conteúdos gerados pelo Cliente na plataforma) são{" "}
              <strong>apagados e destruídos do banco de dados de forma definitiva em até 1 (um) dia
              útil (24 horas úteis)</strong>, incluindo réplicas ativas, sem qualquer possibilidade
              de recuperação. A Kentro não é obrigada a manter cópias após o término do contrato.
            </p>
            <p>
              <strong>Exportação prévia.</strong> Como a eliminação é irreversível, é
              responsabilidade do Cliente exportar previamente, ainda durante o aviso prévio, os
              dados e o histórico que deseje preservar. A Kentro pode apoiar a extração conforme os
              recursos disponíveis na plataforma.
            </p>
            <p>
              <strong>Registros de acesso e logs de auditoria.</strong> Mesmo após a eliminação dos
              dados do Cliente, a Kentro pode manter, por obrigação legal e para fins de segurança,
              registros de acesso a aplicações (art. 15 do Marco Civil da Internet — Lei 12.965/2014,
              por até 6 meses), logs de auditoria, documentos fiscais, contábeis e quaisquer dados
              cuja retenção seja exigida por lei ou para o exercício regular de direitos. Esses
              registros são desvinculados dos dados do Cliente, ficam acessíveis apenas a pessoas
              autorizadas e são eliminados ao final do prazo legal aplicável.
            </p>
            <p>
              <strong>Exceções legais.</strong> Permanecem retidos apenas os dados cuja guarda seja
              autorizada ou exigida pelo art. 16 da LGPD e por outras normas. Esses dados ficam
              restritos à finalidade legal, com acesso limitado, e são eliminados ao final do prazo
              aplicável.
            </p>
            <p>
              Backups eventualmente existentes seguem ciclo de expiração automática e não são
              utilizados para restabelecer dados eliminados.
            </p>
          </Block>

          <Block id="responsabilidade" title="12. Responsabilidade entre as partes">
            <p>
              Cada Parte responde exclusivamente pelo tratamento que realizar, obrigando-se a manter
              a outra indene por perdas decorrentes de ações, omissões ou erros próprios em desacordo
              com a legislação aplicável.
            </p>
            <p>
              O Cliente reconhece que a Kentro, na qualidade de Operadora, não responde
              solidariamente por danos causados pelo tratamento determinado pelo Cliente, exceto nas
              hipóteses de descumprimento da legislação aplicável ou das instruções lícitas e
              razoáveis recebidas, sendo assegurado à Kentro o direito de regresso.
            </p>
          </Block>

          <Block id="dpo" title="13. Encarregado (DPO) e contato">
            <p>
              Para dúvidas, solicitações ou preocupações relacionadas à proteção de dados, fale com o
              nosso Encarregado pela Proteção de Dados Pessoais:{" "}
              <a href="mailto:dpo@kentro.com.br">dpo@kentro.com.br</a>. Relatos de conduta e
              denúncias podem ser feitos, de forma anônima, pelo{" "}
              <Link to="/ouvidoria" className="text-primary-glow hover:underline">
                Canal de Denúncias
              </Link>
              .
            </p>
            <p>
              Esta Política pode ser atualizada para refletir mudanças legais, regulatórias,
              técnicas ou de negócio. A versão vigente está sempre disponível em{" "}
              <a href="https://kentro.com.br/lgpd">kentro.com.br/lgpd</a>.
            </p>
          </Block>
        </div>

        <div className="mt-16 rounded-2xl border border-border bg-card/40 p-6 md:p-8">
          <h2 className="text-lg font-semibold">Documentos relacionados</h2>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link
              to="/termos-de-uso"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <ShieldCheck className="size-4" /> Termos de Uso
            </Link>
            <Link
              to="/privacidade"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <ShieldCheck className="size-4" /> Política de Privacidade
            </Link>
            <Link
              to="/ouvidoria"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-background/40 px-4 py-2 text-sm font-medium hover:bg-foreground/5"
            >
              <Mail className="size-4" /> Canal de Denúncias
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

function Block({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] leading-relaxed text-muted-foreground [&_li]:my-1 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  );
}
